/**
 * DateTime Helpers — Local ↔ UTC conversion
 *
 * Shared utilities for any form that persists datetime values.
 *
 * Convention across the app:
 *  - HTML `datetime-local` inputs produce `YYYY-MM-DDTHH:mm` in the user's
 *    local timezone (no timezone info).
 *  - The server stores UTC and returns ISO-8601 UTC strings
 *    (e.g. `2026-04-15T09:00:00.000Z`).
 *
 * Use `localInputToUtc` when building a payload before sending it to the
 * backend, and `utcToLocalInput` when loading a server value back into a
 * `datetime-local` input for editing.
 */

/**
 * Convert a `datetime-local` input value (local tz, `YYYY-MM-DDTHH:mm`) to a
 * UTC ISO-8601 string for the backend.
 *
 * Returns `undefined` for empty/invalid input so the field can be omitted
 * from the payload (or cleared on the server).
 *
 * @example
 *   // user in IST (UTC+5:30) picks 2026-04-15 14:30
 *   localInputToUtc("2026-04-15T14:30") // => "2026-04-15T09:00:00.000Z"
 */
export function localInputToUtc(localValue: string | null | undefined): string | undefined {
  if (!localValue) return undefined;
  const d = new Date(localValue);
  if (isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

/**
 * Convert a UTC datetime from the server (ISO-8601 string or `Date`) into the
 * `YYYY-MM-DDTHH:mm` format a `datetime-local` input expects, rendered in
 * the user's local timezone.
 *
 * Returns an empty string for empty/invalid input so it can be assigned
 * directly to an input's `value`.
 *
 * @example
 *   // user in IST (UTC+5:30)
 *   utcToLocalInput("2026-04-15T09:00:00.000Z") // => "2026-04-15T14:30"
 */
export function utcToLocalInput(utcValue: string | Date | null | undefined): string {
  if (!utcValue) return "";
  const d = utcValue instanceof Date ? utcValue : new Date(utcValue);
  if (isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}
